import { useEffect, useRef, useState } from 'react';
import { ContentWorkflow, WorkflowState } from '@amzn/awscat-pattern-library-service-model';

import { useQualityCheckService } from '../../contexts/QualityCheckServiceContext';
import { QualityCheckScanResults, ScanState } from '../../services/QualityCheckService';
import { OpenedEdits } from '../../services/InlineEditsManager';

export interface QualityCheckState {
  readonly state: ScanState;
  readonly results: QualityCheckScanResults | null;
  readonly openedEdits: OpenedEdits | undefined;
  readonly isScanConfirmed: boolean;
}

export interface UseQualityCheckStateOptions {
  readonly expectedState?: WorkflowState;
  readonly workflow?: ContentWorkflow;
  readonly contentId?: string;
}

/**
 * Subscribes to the QualityCheckService and returns reactive state.
 *
 * When `expectedState`, `workflow`, and `contentId` are provided, automatically
 * loads existing scan results on first mount if the workflow matches.
 */
export function useQualityCheckState(options?: UseQualityCheckStateOptions): QualityCheckState {
  const service = useQualityCheckService();
  const loadedContentIdRef = useRef<string | undefined>(undefined);

  const [snapshot, setSnapshot] = useState<QualityCheckState>(() => ({
    state: service.state,
    results: service.results,
    openedEdits: service.openedEdits,
    isScanConfirmed: service.isScanConfirmed,
  }));

  // When the service emits, sync local snapshot.
  useEffect(() => {
    setSnapshot({
      state: service.state,
      results: service.results,
      openedEdits: service.openedEdits,
      isScanConfirmed: service.isScanConfirmed,
    });
    return service.on('changed', () => {
      setSnapshot({
        state: service.state,
        results: service.results,
        openedEdits: service.openedEdits,
        isScanConfirmed: service.isScanConfirmed,
      });
    });
  }, [service]);

  // When entering an author stage, load existing quality check results if available.
  const { expectedState, workflow, contentId } = options ?? {};
  useEffect(() => {
    if (!expectedState || !workflow || !contentId) return;
    if (
      loadedContentIdRef.current !== contentId &&
      workflow.draftState === expectedState &&
      snapshot.results === null &&
      snapshot.state === 'idle'
    ) {
      loadedContentIdRef.current = contentId;
      service.loadExistingResults(contentId, workflow.draftWorkflowId);
    }
  }, [workflow, contentId, service, expectedState, snapshot.state, snapshot.results]);

  return snapshot;
}
